import React, { useState, useContext, useEffect } from "react";
import { Button } from "@mui/material";
import SolvedClash from "./SolvedClash";
import ClashToSolve from "./ClashToSolve";
import { deepCopy } from "./modelLogic";
import "./clashing.scss";
import {
  hasProperty,
  produceGroupListWithModelGroups,
} from "../../../utils/helpers";
import { GeneralContext } from "../../../context/GeneralContext";

function GroupClashWindow(props) {
  const {
    clashingGroups,
    clashingModel,
    valueGroups,
    solveClash,
    rejectClash,
  } = props;
  const { recordedErrorLog } = useContext(GeneralContext);

  const [clashingList, setClashingList] = useState(
    clashingGroups !== null ? Object.keys(clashingGroups) : []
  );
  const [solvedList, setSolvedList] = useState([]);
  const [nextToSolve, setNextToSolve] = useState(
    clashingGroups !== null && Object.keys(clashingGroups).length > 0
      ? parseInt(Object.keys(clashingGroups)[0])
      : null
  );
  const [localGroups, setLocalGroups] = useState(
    produceGroupListWithModelGroups(valueGroups, clashingModel)
  );
  const [localModel, setLocalModel] = useState(clashingModel);
  const [usedNewGroups, setUsedNewGroups] = useState([]);
  const [recommendedGroup, setRecommendedGroup] = useState(null);

  // Effect to find the next available group number
  useEffect(() => {
    if (localGroups && localGroups.length > 0) {
      const availableGroups = [];
      
      for (let i = 0; i < localGroups.length; i++) {
        const group = localGroups[i];
        if (group.value === null && !usedNewGroups.includes(group.groupNumber)) {
          availableGroups.push(group.groupNumber);
        }
      }

      if (availableGroups.length > 0) {
        // Sort to get the lowest available group number
        availableGroups.sort((a, b) => a - b);
        setRecommendedGroup(availableGroups[0]);
      } else {
        setRecommendedGroup(null);
      }
    }
  }, [localGroups, usedNewGroups, nextToSolve]);

  const handleFinalSolve = () => {
    solveClash(localGroups, localModel);
  };

  const solve = (groupId, chosenNewGroup, solution) => {
    try {
      const modelCopy = deepCopy(localModel);
      let newGroupToWrite = groupId;
      let updatedGroups = [];

      // Keep track of the used group number if using NEW_GROUP solution
      if (solution === "NEW_GROUP" && chosenNewGroup) {
        setUsedNewGroups((prev) => [...prev, chosenNewGroup]);
        newGroupToWrite = chosenNewGroup;
      }

      // Process model's parameters and structure recursively
      const processModelGroups = (model, groupId, chosenNewGroup, solution) => {
        // Handle non-recurring parameters
        if (model.modelParams) {
          model.modelParams = model.modelParams.map((param) => {
            if (hasProperty(param, "group") && param.group === groupId) {
              let updatedParam = { ...param };
              
              if (solution === "CURRENT_STAYS") {
                updatedParam.value = clashingGroups[groupId].currentValue;
                
                if (clashingGroups[groupId].currentHardMax === null || clashingGroups[groupId].currentHardMax === undefined) {
                  if (hasProperty(updatedParam, "hardMax")) {
                    delete updatedParam.hardMax;
                  }
                } else {
                  updatedParam.hardMax = clashingGroups[groupId].currentHardMax;
                }
                
                if (clashingGroups[groupId].currentHardMin === null || clashingGroups[groupId].currentHardMin === undefined) {
                  if (hasProperty(updatedParam, "hardMin")) {
                    delete updatedParam.hardMin;
                  }
                } else {
                  updatedParam.hardMin = clashingGroups[groupId].currentHardMin;
                }
                
                if (clashingGroups[groupId].currentFixed === null || clashingGroups[groupId].currentFixed === undefined) {
                  updatedParam.customFixed = false;
                } else {
                  updatedParam.customFixed = clashingGroups[groupId].currentFixed;
                }
              } else if (solution === "NEW_GROUP") {
                updatedParam.group = chosenNewGroup;
              }
              
              return updatedParam;
            } else {
              return param;
            }
          });
        }

        // Handle recurring parameters
        if (hasProperty(model, "recParams")) {
          for (let i = 0; i < model.recParams.length; i++) {
            const paramSet = model.recParams[i];

            for (let j = 0; j < paramSet.length; j++) {
              const param = paramSet[j];
              if (hasProperty(param, "group") && param.group === groupId) {
                let updatedParam = { ...model.recParams[i][j] };
                
                if (solution === "CURRENT_STAYS") {
                  updatedParam.value = clashingGroups[groupId].currentValue;
                  
                  if (clashingGroups[groupId].currentHardMax === null || clashingGroups[groupId].currentHardMax === undefined) {
                    if (hasProperty(updatedParam, "hardMax")) {
                      delete updatedParam.hardMax;
                    }
                  } else {
                    updatedParam.hardMax = clashingGroups[groupId].currentHardMax;
                  }
                  
                  if (clashingGroups[groupId].currentHardMin === null || clashingGroups[groupId].currentHardMin === undefined) {
                    if (hasProperty(updatedParam, "hardMin")) {
                      delete updatedParam.hardMin;
                    }
                  } else {
                    updatedParam.hardMin = clashingGroups[groupId].currentHardMin;
                  }
                  
                  if (clashingGroups[groupId].currentFixed === null || clashingGroups[groupId].currentFixed === undefined) {
                    updatedParam.customFixed = false;
                  } else {
                    updatedParam.customFixed = clashingGroups[groupId].currentFixed;
                  }
                  
                  model.recParams[i][j] = updatedParam;
                  
                  if (hasProperty(model, "recTableRows")) {
                    model.recTableRows[i] = {
                      ...model.recTableRows[i],
                      [model.recParams[i][j].name]: clashingGroups[groupId].currentValue,
                    };
                  }
                } else if (solution === "NEW_GROUP") {
                  updatedParam.group = chosenNewGroup;
                  model.recParams[i][j] = updatedParam;
                }
              }
            }
          }
        }

        // Handle lineshape parameters
        if (hasProperty(model, "lineshapeData")) {
          for (let i = 0; i < model.lineshapeData.length; i++) {
            const lineParams = model.lineshapeData[i].lineParams;
            const lineTableRows = hasProperty(model.lineshapeData[i], "lineTableRows") 
              ? model.lineshapeData[i].lineTableRows 
              : [];
              
            for (let j = 0; j < lineParams.length; j++) {
              const paramSet = lineParams[j];
              for (let k = 0; k < paramSet.length; k++) {
                const param = paramSet[k];
                if (hasProperty(param, "group") && param.group === groupId) {
                  let updatedParam = { ...lineParams[j][k] };
                  
                  if (solution === "CURRENT_STAYS") {
                    updatedParam.value = clashingGroups[groupId].currentValue;
                    
                    if (clashingGroups[groupId].currentHardMax === null || clashingGroups[groupId].currentHardMax === undefined) {
                      if (hasProperty(updatedParam, "hardMax")) {
                        delete updatedParam.hardMax;
                      }
                    } else {
                      updatedParam.hardMax = clashingGroups[groupId].currentHardMax;
                    }
                    
                    if (clashingGroups[groupId].currentHardMin === null || clashingGroups[groupId].currentHardMin === undefined) {
                      if (hasProperty(updatedParam, "hardMin")) {
                        delete updatedParam.hardMin;
                      }
                    } else {
                      updatedParam.hardMin = clashingGroups[groupId].currentHardMin;
                    }
                    
                    if (clashingGroups[groupId].currentFixed === null || clashingGroups[groupId].currentFixed === undefined) {
                      updatedParam.customFixed = false;
                    } else {
                      updatedParam.customFixed = clashingGroups[groupId].currentFixed;
                    }
                    
                    lineParams[j][k] = updatedParam;
                    
                    if (hasProperty(model.lineshapeData[i], "lineTemplate") && lineTableRows.length > 0) {
                      if (!lineTableRows[j]) {
                        lineTableRows[j] = {};
                      }
                      lineTableRows[j][lineParams[j][k].name] = clashingGroups[groupId].currentValue;
                    }
                  } else if (solution === "NEW_GROUP") {
                    updatedParam.group = chosenNewGroup;
                    lineParams[j][k] = updatedParam;
                  }
                }
              }
            }
            
            model.lineshapeData[i].lineParams = lineParams;
            if (hasProperty(model.lineshapeData[i], "lineTemplate") && lineTableRows.length > 0) {
              model.lineshapeData[i].lineTableRows = lineTableRows;
            }
          }
        }
        
        // Recursively process submodels
        if (hasProperty(model, "subModels") && model.subModels.length > 0) {
          for (let i = 0; i < model.subModels.length; i++) {
            model.subModels[i] = processModelGroups(model.subModels[i], groupId, chosenNewGroup, solution);
          }
        }
        
        return model;
      };

      // Process the model recursively
      const processedModel = processModelGroups(modelCopy, groupId, chosenNewGroup, solution);

      // Update groups based on the solution type
      switch (solution) {
        case "CURRENT_STAYS":
          updatedGroups = localGroups.map((group) => {
            if (group.groupNumber === groupId) {
              return {
                ...group,
                memberCount:
                  group.memberCount + clashingGroups[nextToSolve].memberCount,
              };
            } else {
              return group;
            }
          });
          break;
          
        case "NEW_STAYS":
          updatedGroups = localGroups.map((group) => {
            if (group.groupNumber === groupId) {
              return {
                ...group,
                value: clashingGroups[groupId].incomingValue,
                hardMax: clashingGroups[groupId].incomingHardMax,
                hardMin: clashingGroups[groupId].incomingHardMin,
                fixed: clashingGroups[groupId].incomingFixed,
                memberCount:
                  group.memberCount + clashingGroups[nextToSolve].memberCount,
              };
            } else {
              return group;
            }
          });
          break;
          
        case "NEW_GROUP":
          newGroupToWrite = chosenNewGroup;
          updatedGroups = localGroups.map((group) => {
            if (group.groupNumber === chosenNewGroup) {
              return {
                ...group,
                value: clashingGroups[groupId].incomingValue,
                hardMax: clashingGroups[groupId].incomingHardMax,
                hardMin: clashingGroups[groupId].incomingHardMin,
                fixed: clashingGroups[groupId].incomingFixed,
                memberCount:
                  group.memberCount + clashingGroups[nextToSolve].memberCount,
              };
            } else {
              return group;
            }
          });
          break;

        default:
          break;
      }
      
      setLocalModel(processedModel);
      setLocalGroups(updatedGroups);

      setSolvedList((old) => [
        ...old,
        {
          currentValue: clashingGroups[nextToSolve].currentValue,
          currentGroup: groupId,
          newVal: clashingGroups[nextToSolve].incomingValue,
          newGroup: newGroupToWrite,
          solution: solution,
          currentHardMax: clashingGroups[nextToSolve].currentHardMax,
          incomingHardMax: clashingGroups[nextToSolve].incomingHardMax,
          currentHardMin: clashingGroups[nextToSolve].currentHardMin,
          incomingHardMin: clashingGroups[nextToSolve].incomingHardMin,
          currentFixed: clashingGroups[nextToSolve].currentFixed,
          incomingFixed: clashingGroups[nextToSolve].incomingFixed,
          conflicts: Array.isArray(clashingGroups[nextToSolve].conflicts) 
            ? clashingGroups[nextToSolve].conflicts 
            : []
        },
      ]);

      const copyOfClashList = [...clashingList];
      copyOfClashList.shift();
      setClashingList(copyOfClashList);

      if (copyOfClashList.length > 0) {
        setNextToSolve(parseInt(copyOfClashList[0]));
      } else {
        setNextToSolve(null);
      }
    } catch (error) {
      recordedErrorLog("Solution processing failure: ", error);
    }
  };

  return (
    <div className="groupClashWindow">
      <div className="solvingSection">
        {solvedList.length > 0 ? (
          <div className="solved">
            <div className="solvedHeader">Clash Solutions:</div>
            {solvedList.map((solution, index) => (
              <SolvedClash
                key={index}
                currentVal={solution.currentValue}
                currentGroup={solution.currentGroup}
                newVal={solution.newVal}
                newGroup={solution.newGroup}
                solution={solution.solution}
                currentHardMax={solution.currentHardMax}
                incomingHardMax={solution.incomingHardMax}
                currentHardMin={solution.currentHardMin}
                incomingHardMin={solution.incomingHardMin}
                currentFixed={solution.currentFixed}
                incomingFixed={solution.incomingFixed}
                conflicts={solution.conflicts}
              />
            ))}
          </div>
        ) : (
          <></>
        )}
        {nextToSolve !== null && clashingGroups !== null ? (
          <div className="toSolve">
            <div className="conterContainer">
              <div className="counterTitle">Clashes left to solve:</div>
              <div className="counterVal">{clashingList.length}</div>
            </div>
            <ClashToSolve
              groupId={nextToSolve}
              groups={localGroups}
              currentValue={clashingGroups[nextToSolve].currentValue}
              incomingValue={clashingGroups[nextToSolve].incomingValue}
              currentHardMax={clashingGroups[nextToSolve].currentHardMax}
              incomingHardMax={clashingGroups[nextToSolve].incomingHardMax}
              currentHardMin={clashingGroups[nextToSolve].currentHardMin}
              incomingHardMin={clashingGroups[nextToSolve].incomingHardMin}
              currentFixed={clashingGroups[nextToSolve].currentFixed}
              incomingFixed={clashingGroups[nextToSolve].incomingFixed}
              conflicts={clashingGroups[nextToSolve].conflicts}
              solve={solve}
              usedNewGroups={usedNewGroups}
              recommendedGroup={recommendedGroup}
            />
          </div>
        ) : (
          <div className="noSolutions">
            All clashing groups have been solved.
          </div>
        )}
      </div>

      <div className="solutionButtons">
        <Button
          variant="contained"
          size="small"
          sx={{ m: 1 }}
          onClick={() => handleFinalSolve()}
          className="solutionButton"
          disabled={clashingList.length !== 0}
        >
          Solve Clash
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={{ m: 1 }}
          onClick={() => rejectClash()}
          className="solutionButton"
        >
          Cancel Load
        </Button>
      </div>
    </div>
  );
}

export default GroupClashWindow;
